import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';

export type DateRange = { start: Date | null; end: Date | null };
export type DayState = 'endpoint' | 'range' | 'default';

const DROPDOWN_WIDTH = 340;
const DATE_FORMAT: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

const toStartOfDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const toEndOfDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

const isSameDay = (d1: Date | null, d2: Date | null): boolean =>
    !!d1 && !!d2 && d1.toDateString() === d2.toDateString();

export const toIsoDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const dayRange = (date: Date): DateRange => ({ start: toStartOfDay(date), end: toEndOfDay(date) });

const monthRange = (ref: Date, monthOffset = 0): DateRange => {
    const year = ref.getFullYear();
    const month = ref.getMonth() + monthOffset;
    return {
        start: toStartOfDay(new Date(year, month, 1)),
        end: toEndOfDay(new Date(year, month + 1, 0)),
    };
};

const DATE_PRESETS = [
    { label: 'Todo período', getRange: (): DateRange => ({ start: null, end: null }) },
    { label: 'Hoje', getRange: () => dayRange(new Date()) },
    {
        label: 'Ontem',
        getRange: () => {
            const date = new Date();
            date.setDate(date.getDate() - 1);
            return dayRange(date);
        },
    },
    {
        label: '7 Dias',
        getRange: () => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 6);
            return { start: toStartOfDay(start), end: toEndOfDay(end) };
        },
    },
    { label: 'Este mês', getRange: () => monthRange(new Date()) },
    { label: 'Mês anterior', getRange: () => monthRange(new Date(), -1) },
] as const;

const rangesMatch = (a: DateRange, b: DateRange): boolean => {
    if (!a.start && !b.start) return true;
    if (!a.start || !b.start) return false;
    if (!isSameDay(a.start, b.start)) return false;
    if (!a.end && !b.end) return true;
    if (!a.end || !b.end) return false;
    return isSameDay(a.end, b.end);
};

const formatDateRangeLabel = (value: DateRange): string => {
    for (const preset of DATE_PRESETS) {
        if (rangesMatch(value, preset.getRange())) return preset.label;
    }
    if (!value.start) return 'Todo período';

    const startStr = value.start.toLocaleDateString('pt-BR', DATE_FORMAT);
    if (!value.end || isSameDay(value.start, value.end)) return startStr;
    return `${startStr} - ${value.end.toLocaleDateString('pt-BR', DATE_FORMAT)}`;
};

const isDateInRange = (day: Date, value: DateRange): boolean => {
    if (!value.start) return false;
    const d = toStartOfDay(day);
    const s = toStartOfDay(value.start);
    if (!value.end) return d.getTime() === s.getTime();
    return d >= s && d <= toStartOfDay(value.end);
};

export const getDayState = (day: Date, value: DateRange): DayState => {
    if (isSameDay(value.start, day) || isSameDay(value.end, day)) return 'endpoint';
    if (value.start && value.end && !isSameDay(value.start, value.end) && isDateInRange(day, value)) {
        return 'range';
    }
    return 'default';
};

const buildMonthMatrix = (viewDate: Date): (Date | null)[][] => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const matrix: (Date | null)[][] = [[]];
    let week = matrix[0];

    for (let i = 0, firstDay = new Date(year, month, 1).getDay(); i < firstDay; i++) week.push(null);

    for (let day = 1, daysInMonth = new Date(year, month + 1, 0).getDate(); day <= daysInMonth; day++) {
        if (week.length === 7) {
            week = [];
            matrix.push(week);
        }
        week.push(new Date(year, month, day));
    }

    while (week.length < 7) week.push(null);
    return matrix;
};

interface UseDateRangePickerProps {
    value: DateRange;
    onChange: (range: DateRange) => void;
}

export const useDateRangePicker = ({ value, onChange }: UseDateRangePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState(new Date());
    const [isSelectingEnd, setIsSelectingEnd] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // fixed + coords de viewport — evita painel cortado no scroll interno (mobile/PWA)
    useLayoutEffect(() => {
        if (!isOpen || !buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownWidth = Math.min(DROPDOWN_WIDTH, window.innerWidth - 32);
        let left = rect.left;

        if (rect.left + dropdownWidth > window.innerWidth - 16) {
            left = Math.max(16, rect.right - dropdownWidth);
        }

        const estimatedHeight = 360;
        const spaceBelow = window.innerHeight - rect.bottom - 16;
        const top =
            spaceBelow < estimatedHeight && rect.top > estimatedHeight
                ? Math.max(16, rect.top - estimatedHeight - 4)
                : rect.bottom + 4;

        setDropdownPosition({ top, left });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setIsSelectingEnd(false);
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (dropdownRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
            setIsOpen(false);
            setIsSelectingEnd(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const changeMonth = (delta: number) => {
        setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + delta, 1));
    };

    const handleDayClick = (day: Date) => {
        const start = toStartOfDay(day);
        const end = toEndOfDay(day);

        if (!isSelectingEnd || (value.start && day < value.start)) {
            onChange({ start, end });
            setIsSelectingEnd(true);
            return;
        }

        onChange({ start: toStartOfDay(value.start!), end });
        setIsSelectingEnd(false);
        setIsOpen(false);
    };

    const presets = useMemo(
        () => DATE_PRESETS.map(({ label, getRange }) => ({
            label,
            action: () => {
                onChange(getRange());
                setIsOpen(false);
            },
        })),
        [onChange],
    );

    const monthYearKey = toIsoDate(viewDate).slice(0, 7);

    return {
        isOpen,
        toggleOpen: () => setIsOpen((open) => !open),
        clearRange: () => onChange({ start: null, end: null }),
        buttonRef,
        dropdownRef,
        dropdownPosition,
        handlePrevMonth: () => changeMonth(-1),
        handleNextMonth: () => changeMonth(1),
        handleDayClick,
        monthMatrix: buildMonthMatrix(viewDate),
        monthLabel: viewDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' }),
        monthYearKey,
        displayLabel: formatDateRangeLabel(value),
        presets,
    };
};
